import React, { useState, useEffect } from "react";
import NavBar from "../Nav_Footer/NavBar";
import Footer from "../Nav_Footer/Footer";
import axios from "axios";
import { Link, Outlet } from "react-router-dom";
import "./Booking.scss";
import useFadeIn from "../hooks/useFadeIn";

const BACKEND = process.env.REACT_APP_BACKEND;

function FadeBoatCard({ children, index = 0 }) {
  const ref = useFadeIn(index * 80);
  return <div ref={ref} className="fade-in">{children}</div>;
}

const fmt12 = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hr = parseInt(h);
  return `${hr % 12 || 12}:${m} ${hr >= 12 ? "PM" : "AM"}`;
};

function Booking() {
  const [maintenanceMode] = useState(false);
  const [boats, setBoats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState({ summerTime: false, timeSlots: [], summerTimeSlots: [] });
  const [holidays, setHolidays] = useState([]);
  const [pricingBoatId, setPricingBoatId] = useState(null);
  const [activeHoliday, setActiveHoliday] = useState(null);

  const todayStr = new Date().toISOString().slice(0, 10);

  // Lock body scroll when holiday modal is open
  useEffect(() => {
    document.body.style.overflow = activeHoliday ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [activeHoliday]);

  const isHolidayActive = (h) =>
    todayStr >= h.start_date.slice(0, 10) && todayStr <= h.end_date.slice(0, 10);

  const getBoatPricing = (boat, destName, isSummer) => {
    const key = destName.toLowerCase();
    return isSummer ? (boat.summerPricing?.[key] || {}) : (boat.pricing?.[key] || {});
  };

  useEffect(() => {
    if (maintenanceMode) return;
    const fetchAll = async () => {
      try {
        const [boatsRes, settingsRes, holidaysRes] = await Promise.all([
          axios.get(`${BACKEND}/api/boats`),
          axios.get(`${BACKEND}/api/settings`),
          axios.get(`${BACKEND}/api/holidays`),
        ]);
        const allBoats = boatsRes.data;
        const available = allBoats.filter((b) => b.status === "available");
        setBoats(allBoats);
        setSettings(settingsRes.data);
        setHolidays(holidaysRes.data);
        if (available.length > 0) setPricingBoatId(available[0]._id);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data");
        setLoading(false);
      }
    };
    fetchAll();
  }, [maintenanceMode]);

  const activeSlots = settings.summerTime
    ? settings.summerTimeSlots || []
    : settings.timeSlots || [];

  const availableBoats = boats.filter((b) => b.status === "available");
  const selectedBoat = boats.find((b) => b._id === pricingBoatId);

  const renderPricingPanel = () => {
    if (!selectedBoat) return null;
    const isSummer = settings.summerTime;
    const slotCount = isSummer ? 2 : 3;

    return (
      <div key={pricingBoatId} className="pricing-panel">
        {selectedBoat.destinations?.map((dest, destIndex) => {
          const p = getBoatPricing(selectedBoat, dest.name, isSummer);
          const slotKeys = Array.from({ length: slotCount }, (_, i) => `slot${i + 1}`);

          return (
            <div
              key={dest._id || dest.name}
              className="pricing-dest-card"
              style={{ animationDelay: `${destIndex * 60}ms` }}
            >
              <h3 className="pricing-dest-card__name">{dest.name}</h3>
              {dest.isSlotBased ? (
                <ul className="pricing-dest-card__list">
                  {slotKeys.map((key, idx) => (
                    <li key={key} className="pricing-row">
                      <span className="pricing-row__label">
                        <span className="pricing-row__slot-name">
                          {activeSlots[idx] ? activeSlots[idx].label : `Slot ${idx + 1}`}
                        </span>
                        {activeSlots[idx] && (
                          <span className="pricing-row__slot-time">
                            {fmt12(activeSlots[idx].start_time)}–{fmt12(activeSlots[idx].end_time)}
                          </span>
                        )}
                      </span>
                      <span className="pricing-row__price">
                        {p[key] ? `EGP ${p[key].toLocaleString()}` : "-"}
                      </span>
                    </li>
                  ))}
                  {p.fullDay > 0 && (
                    <li className="pricing-row pricing-row--highlight">
                      <span className="pricing-row__label">Full Day</span>
                      <span className="pricing-row__price">EGP {p.fullDay.toLocaleString()}</span>
                    </li>
                  )}
                </ul>
              ) : (
                <ul className="pricing-dest-card__list">
                  <li className="pricing-row">
                    <span className="pricing-row__label">Full Trip</span>
                    <span className="pricing-row__price">
                      {p.full ? `EGP ${p.full.toLocaleString()}` : "-"}
                    </span>
                  </li>
                  <li className="pricing-row">
                    <span className="pricing-row__label">Drop-off Only</span>
                    <span className="pricing-row__price">
                      {p.dropOff ? `EGP ${p.dropOff.toLocaleString()}` : "-"}
                    </span>
                  </li>
                </ul>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderHolidayModalBody = () => {
    if (!activeHoliday || !selectedBoat) return null;
    const isSummer = settings.summerTime;
    const holidayBoatPricing = activeHoliday.pricing?.[pricingBoatId];

    if (!holidayBoatPricing) {
      return (
        <p className="holiday-modal__empty">
          No special pricing set for {selectedBoat.name}.
        </p>
      );
    }

    return (
      <div className="holiday-modal__pricing">
        <h3>{selectedBoat.name}</h3>
        {selectedBoat.destinations?.map((dest) => {
          const key = dest.name.toLowerCase();
          const p = holidayBoatPricing[key];
          if (!p) return null;
          const slotCount = isSummer ? 2 : 3;

          return (
            <div key={key} className="holiday-modal__dest">
              <h4>{dest.name}</h4>
              {dest.isSlotBased ? (
                <ul className="pricing-dest-card__list">
                  {Array.from({ length: slotCount }, (_, i) => {
                    const field = isSummer ? `summer_slot${i + 1}` : `slot${i + 1}`;
                    const val = p[field];
                    return (
                      <li key={field} className="pricing-row">
                        <span className="pricing-row__label">
                          <span className="pricing-row__slot-name">
                            {activeSlots[i] ? activeSlots[i].label : `Slot ${i + 1}`}
                          </span>
                          {activeSlots[i] && (
                            <span className="pricing-row__slot-time">
                              {fmt12(activeSlots[i].start_time)}–{fmt12(activeSlots[i].end_time)}
                            </span>
                          )}
                        </span>
                        <span className="pricing-row__price">
                          {val ? `EGP ${val.toLocaleString()}` : "-"}
                        </span>
                      </li>
                    );
                  })}
                  {(() => {
                    const fullField = isSummer ? "summer_fullDay" : "fullDay";
                    return p[fullField] > 0 ? (
                      <li className="pricing-row pricing-row--highlight">
                        <span className="pricing-row__label">Full Day</span>
                        <span className="pricing-row__price">EGP {p[fullField].toLocaleString()}</span>
                      </li>
                    ) : null;
                  })()}
                </ul>
              ) : (
                <ul className="pricing-dest-card__list">
                  <li className="pricing-row">
                    <span className="pricing-row__label">Full Trip</span>
                    <span className="pricing-row__price">
                      {isSummer
                        ? p.summer_full ? `EGP ${p.summer_full.toLocaleString()}` : "-"
                        : p.full ? `EGP ${p.full.toLocaleString()}` : "-"}
                    </span>
                  </li>
                  <li className="pricing-row">
                    <span className="pricing-row__label">Drop-off</span>
                    <span className="pricing-row__price">
                      {isSummer
                        ? p.summer_dropOff ? `EGP ${p.summer_dropOff.toLocaleString()}` : "-"
                        : p.dropOff ? `EGP ${p.dropOff.toLocaleString()}` : "-"}
                    </span>
                  </li>
                </ul>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <NavBar />
      <div className="booking-container">
        {maintenanceMode ? (
          <section className="maintenance-mode">
            <div className="maintenance-content">
              <h1>Technical Difficulties</h1>
              <p>We apologize for the inconvenience. Our booking system is currently undergoing maintenance.</p>
              <p className="contact-message">Please contact us via WhatsApp for booking inquiries:</p>
              <a href="https://wa.me/201224524055" target="_blank" rel="noopener noreferrer" className="whatsapp-button">
                WhatsApp: +20 122 452 4055
              </a>
            </div>
          </section>
        ) : (
          <>
            {/* ── Section 1: Hero ── */}
            <section className="hero hero--revamp">
              <div className="hero__eyebrow">El Gouna Sailing Club</div>
              <h1 className="hero__title">Set Sail on the Red Sea</h1>
              <p className="hero__sub">
                Private boats, island trips &amp; sailing classes: book your adventure.
              </p>
              <div className="hero__scroll-hint" aria-hidden="true">↓</div>
            </section>

            {/* ── Section 2: Pricing ── */}
            <section className="pricing-section" id="pricing">
              <div className="pricing-section__inner">
                <div className="pricing-section__header">
                  <span className="section-label">Pricing</span>
                  <h2>What Does It Cost?</h2>
                  <span className="pricing-section__season-badge">
                    {settings.summerTime ? "☀ Summer Season" : "Regular Season"}
                  </span>
                </div>

                {!loading && availableBoats.length > 0 && (
                  <div className="pricing-tabs">
                    {availableBoats.map((boat) => (
                      <button
                        key={boat._id}
                        className={`pricing-tab${pricingBoatId === boat._id ? " pricing-tab--active" : ""}`}
                        onClick={() => setPricingBoatId(boat._id)}
                      >
                        {boat.name}
                      </button>
                    ))}
                  </div>
                )}

                {!loading && renderPricingPanel()}

                {!loading && holidays.length > 0 && (
                  <div className="holiday-strip">
                    <span className="section-label">Special Pricing Periods</span>
                    <div className="holiday-strip__list">
                      {holidays.map((h) => (
                        <button
                          key={h._id}
                          className={`holiday-chip${isHolidayActive(h) ? " holiday-chip--active" : ""}`}
                          onClick={() => setActiveHoliday(h)}
                        >
                          {isHolidayActive(h) && <span className="holiday-chip__dot" />}
                          {h.holiday_name}
                          <span className="holiday-chip__dates">
                            {new Date(h.start_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                            {" – "}
                            {new Date(h.end_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* ── Section 3: Booking ── */}
              <div className="booking-section__header">
                <span className="section-label">Reserve Your Spot</span>
                <h2>Book a Boat</h2>
              </div>
              <div className="boat-gallery">
                {loading ? (
                  <p>Loading...</p>
                ) : (
                  availableBoats.map((boat, i) => (
                    <FadeBoatCard key={boat._id} index={i}>
                      <Link to={`/booking/${boat._id}`} className="boat-link">
                        <div className="boat-card">
                          <img
                            src={`${BACKEND}/images/boats/${boat.images[0]}`}
                            alt={boat.name}
                            className="boat-image"
                          />
                          <div className="boat-info">
                            <h3>{boat.name}</h3>
                            <p>Type: {boat.type}</p>
                            <p>Capacity: {boat.capacity} people</p>
                          </div>
                        </div>
                      </Link>
                    </FadeBoatCard>
                  ))
                )}
              </div>
              <div className="classes">
                <span className="section-label">On the Water</span>
                <h2>Sailing Classes</h2>
                <Link to="/booking/booking-class">
                  <div className="boat-card">
                    <img
                      src={`${BACKEND}/images/ActivitiesWebpComp/sailing_class.webp`}
                      alt="Sailing Class"
                      className="boat-image"
                    />
                    <div className="boat-info">
                      <h3>Book a Sailing Class</h3>
                      <p>Suitable for all ages</p>
                    </div>
                  </div>
                </Link>
              </div>
          </>
        )}
      </div>

      {/* ── Holiday Detail Modal ── */}
      {activeHoliday && (
        <div
          className="holiday-modal-overlay"
          onClick={(e) => {
            if (e.target.classList.contains("holiday-modal-overlay")) setActiveHoliday(null);
          }}
        >
          <div className="holiday-modal">
            <button className="holiday-modal__close" onClick={() => setActiveHoliday(null)}>
              ×
            </button>
            <div className="holiday-modal__header">
              <h2 className="holiday-modal__name">{activeHoliday.holiday_name}</h2>
              <p className="holiday-modal__range">
                {new Date(activeHoliday.start_date).toLocaleDateString("en-US", {
                  year: "numeric", month: "long", day: "numeric",
                })}
                {" – "}
                {new Date(activeHoliday.end_date).toLocaleDateString("en-US", {
                  year: "numeric", month: "long", day: "numeric",
                })}
              </p>
              {isHolidayActive(activeHoliday) && (
                <span className="holiday-modal__active-badge">Active Now</span>
              )}
            </div>
            {renderHolidayModalBody()}
          </div>
        </div>
      )}

      <Footer />
      <Outlet />
    </>
  );
}

export default Booking;
